/**
 * Mantener vivos los enlaces de los `.md` cuando algo se mueve.
 *
 * Es el gemelo de `canvasRefs.ts`, y hace falta por la misma razón: un enlace
 * (o una imagen) se escribe como ruta relativa, así que mover CUALQUIERA de las
 * dos puntas lo rompe. Hay dos casos y este barrido cubre los dos con la misma
 * regla:
 *
 *   1. se movió el destino → los documentos que lo nombran tienen que apuntar
 *      a su ruta nueva;
 *   2. se movió el documento → sus enlaces a lo que NO se movió tienen que
 *      recalcularse desde su carpeta nueva (`otra.md` pasa a `../otra.md`).
 *
 * La regla única: resolver cada destino contra el estado ANTERIOR al movimiento
 * y volver a escribirlo desde el estado nuevo.
 *
 * ── La propiedad de seguridad ──────────────────────────────────────────────
 * Esto reescribe archivos que escribió el usuario y de los que no hay copia.
 * Por eso el barrido es deliberadamente cobarde:
 *
 *   - **Nada que no se resuelva se toca.** Si el destino no apunta a un archivo
 *     que existía de verdad, se deja exactamente como está. Un enlace raro, un
 *     paréntesis mal balanceado o una sintaxis que este parser no entiende
 *     sobreviven intactos.
 *   - **No se mira adentro del código.** Los bloques cercados y los tramos
 *     entre comillas invertidas se enmascaran antes de buscar: un enlace de
 *     ejemplo dentro de un bloque de código es texto, no un enlace.
 *   - **Sólo se reemplaza el destino**, nunca el texto visible ni el resto de
 *     la línea.
 *   - **El plan se calcula antes de escribir**, para poder mostrarlo y que la
 *     escritura sea una decisión y no una sorpresa (ver `main.ts`).
 */

import { type VaultTreeNode } from "../model/types.ts";
import { type VaultAdapter } from "./VaultAdapter.ts";
import { retargetPath } from "./canvasRefs.ts";
import {
  hrefFor,
  isExternalHref,
  makePathIndex,
  resolveHref,
  splitHash,
  type PathIndex,
} from "./links.ts";

/** Un destino escrito en el documento: dónde está y qué dice. */
interface RefSpan {
  from: number;
  to: number;
  href: string;
}

/** Un archivo a reescribir, ya con su contenido nuevo listo. */
export interface MarkdownRefFix {
  path: string;
  /** El documento completo, con los destinos corregidos. */
  text: string;
  /** Cuántos destinos cambian. Es lo que se le muestra al usuario. */
  count: number;
}

/** Carácter con el que se tapan los tramos de código antes de buscar enlaces. */
const MASK = " ";

/**
 * Devuelve una copia de `text` con el código tapado: mismo largo, mismas
 * posiciones, pero sin nada que pueda parecerse a un enlace. Trabajar sobre la
 * copia deja los índices válidos contra el original.
 */
function maskCode(text: string): string {
  const chars = [...text];
  const mask = (from: number, to: number): void => {
    for (let i = from; i < to && i < chars.length; i++) chars[i] = MASK;
  };

  // Bloques cercados. El cierre tiene que ser del mismo carácter que la
  // apertura; uno sin cerrar tapa hasta el final, que es lo que hace cualquier
  // renderizador de markdown.
  let offset = 0;
  let fence: string | null = null;
  for (const line of text.split("\n")) {
    const m = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (fence === null) {
      if (m) {
        fence = m[1][0];
        mask(offset, offset + line.length);
      }
    } else {
      mask(offset, offset + line.length);
      if (m && m[1][0] === fence) fence = null;
    }
    offset += line.length + 1;
  }

  // Tramos entre comillas invertidas: abren con N y cierran con exactamente N.
  const masked = chars.join("");
  let i = 0;
  while (i < masked.length) {
    if (masked[i] !== "`") {
      i++;
      continue;
    }
    let open = 0;
    while (masked[i + open] === "`") open++;
    let j = i + open;
    let closed = false;
    while (j < masked.length) {
      if (masked[j] !== "`") {
        j++;
        continue;
      }
      let run = 0;
      while (masked[j + run] === "`") run++;
      if (run === open) {
        mask(i, j + run);
        i = j + run;
        closed = true;
        break;
      }
      j += run;
    }
    if (!closed) i += open; // sin cierre: no es código, es texto
  }

  return chars.join("");
}

/**
 * Enlaces inline `[texto](destino)`, imágenes `![alt](src)` y definiciones de
 * referencia `[etiqueta]: destino`.
 *
 * Los destinos con paréntesis o con espacios sin `<>` quedan afuera a
 * propósito: son justo los que un reemplazo ingenuo rompería, y dejar uno sin
 * arreglar es mucho más barato que arruinarlo.
 */
function refSpans(text: string): RefSpan[] {
  const haystack = maskCode(text);
  const spans: RefSpan[] = [];

  const INLINE =
    /!?\[(?:[^\[\]\\]|\\.)*\]\(\s*(?:<([^<>\n]*)>|([^\s()<>]*))(?:\s+(?:"[^"]*"|'[^']*'|\([^()]*\)))?\s*\)/dg;
  const REFDEF = /^ {0,3}\[(?:[^\[\]\\]|\\.)+\]:[ \t]*(?:<([^<>\n]*)>|(\S+))/dgm;

  for (const re of [INLINE, REFDEF]) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(haystack))) {
      const at = m.indices?.[1] ?? m.indices?.[2];
      const href = m[1] ?? m[2];
      if (!at || href === undefined) continue;
      spans.push({ from: at[0], to: at[1], href });
    }
  }

  // Los dos barridos se cruzan, así que el resultado se ordena para que el
  // reemplazo pueda ir de una sola pasada por el texto.
  return spans.sort((a, b) => a.from - b.from);
}

/** La ruta que `path` tenía antes de que `from` se moviera a `to`. */
function oldPathOf(path: string, from: string, to: string): string {
  if (path === to) return from;
  if (path.startsWith(`${to}/`)) return `${from}${path.slice(to.length)}`;
  return path;
}

/** El índice del vault tal como era ANTES del movimiento. */
function oldIndex(tree: VaultTreeNode[], from: string, to: string): PathIndex {
  const index: PathIndex = new Map();
  for (const path of makePathIndex(tree).values()) {
    const old = oldPathOf(path, from, to);
    index.set(old.toLowerCase(), old);
  }
  return index;
}

/** Cada `.md` del árbol, en profundidad. */
function markdownPaths(nodes: VaultTreeNode[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    if (n.children) out.push(...markdownPaths(n.children));
    else if (n.kind === "md") out.push(n.path);
  }
  return out;
}

/**
 * El documento con sus destinos corregidos, o null si no cambia ninguno.
 *
 * `oldDoc` y `newDoc` son la misma ruta salvo que el documento se haya movido
 * él mismo; `index` describe el vault de antes del movimiento.
 */
export function rewriteMarkdownRefs(
  text: string,
  oldDoc: string,
  newDoc: string,
  index: PathIndex,
  from: string,
  to: string,
): { text: string; count: number } | null {
  const docMoved = oldDoc !== newDoc;
  let out = "";
  let last = 0;
  let count = 0;

  for (const span of refSpans(text)) {
    if (!span.href || isExternalHref(span.href)) continue;
    const resolved = resolveHref(oldDoc, span.href, index);
    // Lo que no se resuelve contra el vault de antes no se toca: es la
    // propiedad de seguridad de este módulo.
    if (!resolved) continue;

    const moved = retargetPath(resolved, from, to);
    // Ni el destino se movió ni el documento: no hay nada que arreglar, y
    // reescribirlo igual convertiría un renombrado ajeno en un diff enorme.
    if (moved === null && !docMoved) continue;

    const next = hrefFor(newDoc, moved ?? resolved, splitHash(span.href).hash);
    if (next === span.href) continue;

    out += text.slice(last, span.from) + next;
    last = span.to;
    count++;
  }

  return count === 0 ? null : { text: out + text.slice(last), count };
}

/**
 * Qué habría que reescribir en el vault después de mover `from` a `to`. No
 * escribe nada: devuelve el plan para que quien lo pidió lo confirme.
 *
 * Se llama DESPUÉS del movimiento (el árbol ya trae las rutas nuevas) y
 * reconstruye el estado anterior invirtiendo el movimiento, que es exacto:
 * mover es biyectivo sobre las rutas.
 */
export async function planMarkdownRefFixes(
  adapter: VaultAdapter,
  tree: VaultTreeNode[],
  from: string,
  to: string,
): Promise<MarkdownRefFix[]> {
  const index = oldIndex(tree, from, to);
  const fixes: MarkdownRefFix[] = [];
  for (const path of markdownPaths(tree)) {
    let text: string;
    try {
      text = await adapter.read(path);
    } catch {
      continue; // un documento ilegible no aborta el resto del plan
    }
    const next = rewriteMarkdownRefs(text, oldPathOf(path, from, to), path, index, from, to);
    if (next) fixes.push({ path, text: next.text, count: next.count });
  }
  return fixes;
}

/** Escribe el plan. Devuelve las rutas que no se pudieron escribir. */
export async function applyMarkdownRefFixes(
  adapter: VaultAdapter,
  fixes: MarkdownRefFix[],
): Promise<string[]> {
  const failed: string[] = [];
  for (const fix of fixes) {
    try {
      await adapter.write(fix.path, fix.text);
    } catch (err) {
      console.error("link rewrite failed", fix.path, err);
      failed.push(fix.path);
    }
  }
  return failed;
}
